import classnames from 'classnames'
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { CSSProperties, SyntheticEvent, useRef } from 'react'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
// Utils
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import Link from '@src/quince-site-common/src/domains/Link'
import checkIsInFirstFold from '@src/quince-site-common/src/utils/checkIsInFirstFold'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent } from '@src/telemetry/track'
import { curry } from '@src/utils/functions/utils'

import { THEME_TYPES } from '../../../../quince-site-common/src/constants/themeTypes'
import useDeviceInfo, { DEVICE_TYPES } from '../../../../quince-site-common/src/hooks/useDeviceInfo'
import { RichText, RichTextProps } from '../../molecules'
import { Button, ButtonProps } from '../button'
import { Image, ImageProps } from '../image'
import Swiper from '../swiper'
import { ARROW_COLOR, NAV_POSITION } from '../swiper/Swiper'

import { THEME_TYPES_TO_CLASSNAME_MAPPING } from './featureSectionOne.constant'
import styles from './featureSectionOne.module.scss'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'
export interface FeatureSectionOneProps {
  style: CSSProperties
  headingRichTextProps?: RichTextProps
  contentRichTextProps?: RichTextProps
  subHeadingRichTextProps?: RichTextProps
  descriptionRichTextProps?: RichTextProps
  topLeftImageProps?: ImageProps
  topRightImageProps?: ImageProps
  sliderImages?: ImageProps[]
  iconImageProps?: ImageProps
  buttonProps?: ButtonProps
  className?: string
  themeType?: THEME_TYPES
  link?: Link
  trackingMetaData?: TrackingMetaData
}

const trackClick = (trackingMetaData: TrackingMetaData, isInFirstFold: boolean) => {
  trackClickEvent(TELEMETRY_EVENT_NAMES.LINK_CLICK, {
    trackInternal: true,
    context: {
      ...trackingMetaData.context,
      isInFirstFold: isInFirstFold,
    },
  })
  setFindingMethod[FINDING_METHOD_AGGREGATE.CAMPAIGN]?.({
    elementName: trackingMetaData?.context?.contentContainerName || '',
    elementId: trackingMetaData?.context?.trackingId || '',
  })
  return true
}

const handleClickBaseFunction = (navigationPath: string, router: AppRouterInstance, event: SyntheticEvent) => {
  const targetElement = event.target as Element
  const carouselButtons = Array.from(document.querySelectorAll(`.${styles.featureImagesSlider} button`))

  carouselButtons.every(button => !button.contains(targetElement)) &&
    !event.isHandled &&
    navigationPath &&
    router.push(navigationPath)
}

const handleClick = curry(handleClickBaseFunction, 3)

export const FeatureSectionOne = ({ className = '', ...props }: FeatureSectionOneProps) => {
  const deviceInfo = useDeviceInfo()
  const router = useRouter()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const themeType = props.themeType
  const wrapClassnames = classnames(
    styles.featureSectionOneWrap,
    props.link?.navigationPath && styles.featureSectionOneWrap__clickable,
    {
      [styles[`featureSectionOneWrap__${THEME_TYPES_TO_CLASSNAME_MAPPING[themeType]}`]]:
        themeType === THEME_TYPES.BG_BLACK__CLR_WHITE__CTABG_WHITE__CTACLR_BLACK,
    },
    className,
  )
  const showWhiteColorArrow = themeType && themeType === THEME_TYPES.BG_BLACK__CLR_WHITE__CTABG_WHITE__CTACLR_BLACK

  const carouselImage1MaxWidth = getMaxWidthInPx({
    maxWidth: styles.carouselImage1MaxWidth,
    deviceType: deviceInfo.deviceType,
  })

  const carouselImage2MaxWidth = getMaxWidthInPx({
    maxWidth: styles.carouselImage2MaxWidth,
    deviceType: deviceInfo.deviceType,
  })

  const topLeftImageMaxWidth = getMaxWidthInPx({
    maxWidth: styles.topLeftImageMaxWidth,
    deviceType: deviceInfo.deviceType,
  })

  const topRightImageMaxWidth = getMaxWidthInPx({
    maxWidth: styles.topRightImageMaxWidth,
    deviceType: deviceInfo.deviceType,
  })
  const carouselMaxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: styles.carouselMaxWidthMobile,
    maxWidthDesktopInRem: styles.carouselMaxWidth,
    deviceType: deviceInfo.deviceType,
  })

  const iconMaxWidth = getMaxWidthInPx({
    maxWidth: styles.iconMaxWidth,
    deviceType: deviceInfo.deviceType,
  })

  const containerRef = useRef()
  const isInFirstFold = containerRef.current ? checkIsInFirstFold(containerRef.current) : undefined

  return (
    <div
      style={props.style}
      className={wrapClassnames}
      onClick={
        props.link?.navigationPath
          ? () => {
              trackClick(props.trackingMetaData, isInFirstFold)
              handleClick(props.link?.navigationPath, router)
            }
          : undefined
      }
    >
      <div className={styles.featureSectionOneTop}>
        {isMobile ? (
          <>
            <Swiper
              id={'feature-section-one-carousal'}
              freeMode={false}
              variableWidth
              numberOfVisibleSlides={1}
              showNavigator={isDesktop}
              showDots={!isMobile}
            >
              <Image
                {...props.topLeftImageProps}
                className={classnames(props.topLeftImageProps.className, styles.carouselImage1, 'padding-right--22')}
                maxWidth={carouselImage1MaxWidth}
              />
              <Image
                {...props.topRightImageProps}
                className={classnames(props.topLeftImageProps.className, styles.carouselImage2, 'padding-right--22')}
                maxWidth={carouselImage2MaxWidth}
              />
            </Swiper>
            {props.headingRichTextProps && (
              <RichText
                {...props.headingRichTextProps}
                className={classnames(props.headingRichTextProps.className, styles.heading)}
              />
            )}
            {props.contentRichTextProps && (
              <RichText
                {...props.contentRichTextProps}
                className={classnames(props.contentRichTextProps.className, styles.content)}
              />
            )}
          </>
        ) : (
          <>
            {props.topLeftImageProps.url && (
              <Image {...props.topLeftImageProps} className={styles.topLeftImage} maxWidth={topLeftImageMaxWidth} />
            )}
            <div className={styles.topRightBlock}>
              {props.topRightImageProps.url && (
                <Image
                  {...props.topRightImageProps}
                  className={styles.topRightImage}
                  maxWidth={topRightImageMaxWidth}
                />
              )}
              {props.headingRichTextProps && (
                <div className={styles.headingWrap}>
                  <RichText
                    {...props.headingRichTextProps}
                    className={classnames(props.headingRichTextProps.className, styles.heading)}
                  />
                </div>
              )}{' '}
              {props.contentRichTextProps && (
                <div className={styles.contentWrap}>
                  <RichText
                    {...props.contentRichTextProps}
                    className={classnames(props.contentRichTextProps.className, styles.content)}
                  />
                </div>
              )}{' '}
            </div>
          </>
        )}
      </div>
      <div className={styles.featureSectionOneBottom}>
        <div style={{ position: 'relative' }}>
          <Swiper
            id="feature-section-one-images-carousal"
            showNavigator={isDesktop}
            freeMode={false}
            variableWidth
            numberOfVisibleSlides={1}
            className={styles.featureImagesSlider}
            navPosition={NAV_POSITION.LEFT}
            arrowColor={showWhiteColorArrow ? ARROW_COLOR.WHITE : ARROW_COLOR.BLACK}
          >
            {props.sliderImages?.map((imageProps: ImageProps) => (
              <Image
                key={imageProps.id}
                {...imageProps}
                className={styles.sliderImageItem}
                maxWidth={carouselMaxWidth}
              />
            ))}
          </Swiper>
        </div>
        {(props.iconImageProps?.url ||
          props.subHeadingRichTextProps ||
          props.buttonProps ||
          props.descriptionRichTextProps) && (
          <div className={styles.bottomLeftContent}>
            <div className={styles.leftContentInner}>
              {props.iconImageProps?.url && (
                <Image {...props.iconImageProps} className={styles.leftContentInner__image} maxWidth={iconMaxWidth} />
              )}
              {props.subHeadingRichTextProps && (
                <RichText
                  {...props.subHeadingRichTextProps}
                  className={classnames(props.subHeadingRichTextProps.className, styles.heading)}
                />
              )}
              {props.descriptionRichTextProps && <RichText {...props.descriptionRichTextProps} />}
              {props.buttonProps && (
                <Button
                  {...props.buttonProps}
                  className={classnames(props.buttonProps.className, styles.leftContentInner__button)}
                  trackConfig={{
                    trackInternal: true,
                    category: '',
                    action: '',
                    context: props.trackingMetaData?.context,
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FeatureSectionOne
